import { useEffect, useState } from 'react';
import { Info, Loader2 } from 'lucide-react';
import { formatCents } from '@mdu-invoicing/shared';
import { apiFetch, ApiError } from '@/lib/api-client';
import { Card } from '@/components/ui/Card';

// The §4 tariff matrix is NOT bundled into the SPA — a provider could otherwise
// extract competitors' rates from the JS. It is fetched, role-scoped, from
// GET /tariffs (STAFF = all columns; PROVIDER = its own column only; the route
// 403s a user without canViewAmounts). See packages/api .../tariff-matrix.service.ts.
interface ScopedTariffRow {
  serviceType: string;
  // Only the caller's scoped columns are present (Partial mirrors the server type),
  // so `rates[column]` is `number | undefined` and the rateCell guard is meaningful.
  rates: Partial<Record<string, number>>;
}
interface ScopedTariffGrid {
  columns: string[];
  rows: ScopedTariffRow[];
}

// What drives each service-type row (spec §4 — public quadrant→service mapping,
// not a rate; kept client-side, keyed on the service-type label from the API).
const QUADRANT_HINT: Record<string, string> = {
  'Basic site visit': 'SDU / QM / QP',
  'Early end visit': 'Propriétaire / syndic injoignable',
  'Outdoor site visit': 'QD',
  'Indoor visit — up to 5F': 'QC (≤ 5 étages)',
  'Indoor visit — 6F+': 'QA (≥ 6 étages)',
  'Signed TSA': 'TSA signée',
};

/** Render a rate cell. formatCents throws on a non-integer (incl. null/NaN/float),
 *  so guard with Number.isInteger and fall back to '—' (mirrors Eligibles.tsx). The
 *  server fills every scoped column with an integer; this only fires on a missing
 *  key or a loosened contract. */
function rateCell(cents: number | undefined): string {
  return typeof cents === 'number' && Number.isInteger(cents) ? formatCents(cents) : '—';
}

export function Tariffs() {
  const [grid, setGrid] = useState<ScopedTariffGrid | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiFetch<ScopedTariffGrid>('/tariffs')
      .then((g) => {
        if (cancelled) return;
        setGrid(g);
        setError(null);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : 'Erreur de chargement');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-500">
        Grille tarifaire de référence (spec §4). Le <span className="font-medium text-slate-700">Design Quadrant</span>{' '}
        d'un enregistrement détermine la ligne de service ; le contractant détermine la colonne. Montants en euros.
      </p>

      {loading ? (
        <Card className="flex items-center gap-2 p-5 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin text-brand-purple" />
          Chargement de la grille tarifaire…
        </Card>
      ) : error ? (
        <Card className="p-5 text-sm text-rose-600">
          Impossible de charger la grille tarifaire : {error}.
        </Card>
      ) : grid && grid.columns.length === 0 ? (
        <Card className="p-5 text-sm text-slate-500">
          Aucune colonne tarifaire n'est associée à votre compte.
        </Card>
      ) : grid ? (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto custom-scrollbar">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3">Type de prestation</th>
                  <th className="px-4 py-3">Quadrant / déclencheur</th>
                  {grid.columns.map((c) => (
                    <th key={c} className="px-4 py-3 text-right">
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {grid.rows.map((row) => (
                  <tr key={row.serviceType} className="hover:bg-slate-50/60">
                    <td className="whitespace-nowrap px-4 py-2.5 font-semibold text-slate-800">
                      {row.serviceType}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-xs text-slate-500">
                      {QUADRANT_HINT[row.serviceType] ?? ''}
                    </td>
                    {grid.columns.map((c) => (
                      <td
                        key={c}
                        className="whitespace-nowrap px-4 py-2.5 text-right font-semibold tabular-nums text-slate-800"
                      >
                        {rateCell(row.rates[c])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {/* Notes */}
      <Card className="space-y-2 p-4 text-xs text-slate-500">
        <div className="flex items-start gap-2">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-purple" />
          <p>
            <span className="font-semibold text-slate-700">Keen &amp; Wise</span> est toujours facturé au tarif
            multi-DA (décision OQ-11 ; la colonne « 1-DA » de la source n'est pas utilisée).
          </p>
        </div>
        <div className="flex items-start gap-2">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
          <p>
            Certaines valeurs réelles de <span className="font-medium text-slate-700">Design Quadrant</span> (p.ex.
            <span className="font-mono"> ONLY BUDI</span>) ne figurent pas
            encore dans la grille §4 — ces enregistrements sont signalés « non tarifé » en attendant la confirmation
            des valeurs par l'équipe data.
          </p>
        </div>
      </Card>
    </div>
  );
}
